
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { formatStatesDisplay } from '@/lib/utils';
import type { PEOProvider } from '@/types/peo.types';

interface ProviderCardProps {
  provider: PEOProvider;
  onClick: (provider: PEOProvider) => void;
  onManageOrganization?: (provider: PEOProvider) => void;
}

export const ProviderCard = ({ provider, onClick, onManageOrganization }: ProviderCardProps) => {
  const handleClick = () => {
    console.log('ProviderCard clicked:', provider.name, provider.id);
    // Use onManageOrganization as the primary action, fallback to onClick for backward compatibility
    if (onManageOrganization) {
      onManageOrganization(provider);
    } else {
      onClick(provider);
    }
  };

  return (
    <Card 
      key={provider.id}
      className="glass-card overflow-hidden h-auto group cursor-pointer transform transition-all duration-300 hover:translate-y-[-3px] hover:shadow-elevation-2"
      onClick={handleClick}
    >
      <CardHeader className="p-3 bg-gradient-to-r from-gray-50 to-white border-b">
        <CardTitle className="text-sm font-medium truncate group-hover:text-primary transition-colors">
          {provider.name}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-3 pt-2">
        <div className="space-y-2">
          <div className="flex flex-wrap gap-1.5">
            {provider.is_esac_accredited && (
              <Badge className="bg-primary/10 text-primary hover:bg-primary/20 text-xs border-primary/20 shadow-sm">
                ESAC
              </Badge>
            )}
            {provider.napeo_member && (
              <Badge className="bg-secondary/20 text-secondary-foreground hover:bg-secondary/30 text-xs border-secondary/30 shadow-sm">
                NAPEO
              </Badge>
            )}
            {provider.irs_certified && (
              <Badge className="bg-accent/20 text-accent-foreground hover:bg-accent/30 text-xs border-accent/30 shadow-sm">
                IRS
              </Badge>
            )}
          </div>
          
          <div className="text-xs text-muted-foreground space-y-1.5">
            {provider.years_in_business && (
              <div className="flex justify-between">
                <span className="font-medium text-gray-700">Years:</span> 
                <span>{provider.years_in_business}</span>
              </div>
            )}
            
            {provider.employee_range && (
              <div className="flex justify-between">
                <span className="font-medium text-gray-700">Employees:</span> 
                <span>{provider.employee_range}</span>
              </div>
            )}
            
            {provider.states_served && provider.states_served.length > 0 && (
              <div className="flex justify-between">
                <span className="font-medium text-gray-700">States:</span> 
                <span className="text-right">{formatStatesDisplay(provider.states_served)}</span>
              </div>
            )}
          </div>
          
          <div className="flex flex-wrap gap-1.5 pt-1">
            {provider.mobile_app && (
              <Badge className="bg-gray-100 text-gray-800 hover:bg-gray-200 text-xs border-gray-200 shadow-sm">
                Mobile
              </Badge>
            )}
            {provider.client_portal && (
              <Badge className="bg-gray-100 text-gray-800 hover:bg-gray-200 text-xs border-gray-200 shadow-sm">
                Portal
              </Badge>
            )}
            {provider.international_capabilities && (
              <Badge className="bg-indigo-100 text-indigo-800 hover:bg-indigo-200 text-xs border-indigo-200 shadow-sm">
                Int'l
              </Badge>
            )}
            {provider.startup_friendly && (
              <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-200 text-xs border-emerald-200 shadow-sm">
                Startup
              </Badge>
            )}
            {provider.nonprofit_experience && (
              <Badge className="bg-rose-100 text-rose-800 hover:bg-rose-200 text-xs border-rose-200 shadow-sm">
                NonProfit
              </Badge>
            )}
          </div>
          
        </div>
      </CardContent>
    </Card>
  );
};
